'use client'
import { Link, Typography } from '@mui/material'
import HeadingWithParagraphs from '@/UI/Paragraph/HeadingWithParagraphs'
import HeadingWithList from '@/UI/Paragraph/HeadingWithList'
import CarBackdrop from '@/components/CarBackdrop/CarBackdrop'

const PRIVACY_POLICY_URL = 'https://www.zuto.com/privacy'

const disclaimerListItems = [
  {
    content:
      'Use of this Site, Third Party Websites, links to the Third Party Websites, and any information available via the Site is at your own risk. To the maximum extent permitted by law Zuto disclaims all liability whatsoever, whether arising in contract, tort (including negligence) or otherwise in relation to this Site, Third Party Websites, links to Third Party Websites, and information available via the Site.'
  },
  {
    content:
      'We do our best to ensure that the Site operates properly at all times, but we make no warranties as to the availability or accessibility of the Site, and (save as otherwise set out in these Terms and Conditions) we will not be liable for any damages, loss, costs or expenses incurred by you as a result of any lack of availability or accessibility of the Site.'
  },
  {
    content: (
      <>
        Zuto will not be liable, in contract, tort (including, without limitation, negligence),
        under statute or otherwise, as a result of or in connection with this Site, any Third Party
        Websites, links to Third Party Websites, the information or any products or services offered
        on or through this Site or Third Party Website, whether by Zuto or on its behalf, for any:
        <ul>
          <li>
            economic loss (including, without limitation, loss of revenues, profits, contracts,
            business or anticipated savings); or
          </li>
          <li>loss of goodwill or reputation; or</li>
          <li>special or indirect or consequential loss.</li>
        </ul>
      </>
    )
  },
  {
    content:
      'If Zuto is liable to you directly or indirectly in relation to this Site, any Third Party Websites, links to Third Party Websites, information on the Site or any products or services offered on or through this Site or Third Party Website, that liability (howsoever arising) shall be limited to £100.'
  },
  {
    content:
      'Zuto will not be liable for any loss, damage, expense, costs, delays or other liability whatsoever (including without limitation any financial losses such as loss of profit) which you may incur as a result of any event beyond our reasonable control (including without limitation any failure of transmission, communication, computer or other facilities or your inability to access the Site for any reason or any failure, error or delay in the sending or receiving of any notice or communication or instruction through the post or any electronic medium).'
  },
  {
    content:
      'Nothing in these Terms and Conditions shall be construed as excluding or limiting the liability of Zuto or its group companies for death or personal injury caused by its negligence or for any other liability which cannot be excluded by English law.'
  },
  {
    content:
      'Where you deal as a consumer, nothing in the above exclusions affects your statutory rights.'
  }
]

const PageContent = () => {
  return (
    <>
      <Typography variant="h1" sx={{ fontSize: { xs: '32px', sm: '50px' }, mb: 8 }}>
        Terms and Conditions
      </Typography>
      <HeadingWithParagraphs
        title="Acceptance of Terms"
        styleOverrides={{ marginBottom: 8 }}
        paragraphs={
          <>
            These are the website terms and conditions (&ldquo;Terms and Conditions&rdquo;) of Zuto
            Ltd (&ldquo;Zuto&rdquo;). Please read these Terms and Conditions carefully, they set out
            the basis on which you are allowed to use this website (the &ldquo;Site&rdquo;) and the
            information contained within it. They protect you as a user of the Site and prevent the
            Site from being used for any illegal or unauthorised purposes. By proceeding with access
            to this Site, you are deemed to have accepted both these Terms and Conditions and our{' '}
            <Link href={PRIVACY_POLICY_URL} role="link">
              Privacy Policy
            </Link>
            . If you do not wish to be bound by these terms you should not use the Site.
          </>
        }
      />
      <HeadingWithParagraphs
        title="Information on our Site"
        paragraphs={[
          'We do our best to ensure that the information on the Site is accurate and helpful at all times. However, the information is general in nature, and is intended as a guide only to the types of products and services offered by Zuto. Users are advised to consult with Zuto and to check any product or service information for specific information.'
        ]}
        styleOverrides={{ marginBottom: 8 }}
      />
      <HeadingWithParagraphs
        title="Your use of our Site"
        paragraphs={[
          'You must not under any circumstances seek to undermine the security of the Site or any information submitted to or available through it. In particular, but without limitation, you must not seek to access, alter or delete any information to which you do not have authorised access, seek to overload the system via spamming or flooding, take any action or use any device, routine or software to crash, delay, damage or otherwise interfere with the operation of this Site or attempt to decipher, disassemble or modify any of the software, coding or information comprised in the Site. You are solely responsible for any information submitted by you to our site. You are responsible for ensuring that all information supplied by you is accurate, up-to-date and not misleading or likely to mislead or deceive.'
        ]}
        styleOverrides={{ marginBottom: 8 }}
      />
      <HeadingWithParagraphs
        title="Links to Third Party Sites"
        paragraphs={[
          'This Site may include links to other websites which are not owned, operated, or controlled by Zuto ("Third Party Websites"). Zuto provides you with such links solely for your convenience. The inclusion of such links does not mean or imply that Zuto endorses the Third Party Websites, its availability or contents or any agreement or understanding you enter into with a third party through a Third Party Website. Zuto does not accept any liability for any loss, damage, expense, costs or liability whatsoever incurred by you in respect of these Third Party Websites.'
        ]}
        styleOverrides={{ marginBottom: 8 }}
      />

      <HeadingWithParagraphs
        title="Use of your information"
        styleOverrides={{ marginBottom: 8 }}
        paragraphs={
          <>
            Zuto Ltd (&ldquo;we&rdquo;, &ldquo;us&rdquo;) and potential lenders, insurers, service
            providers, vehicle suppliers and agents will use your personal details (including
            sensitive personal data) and information we obtain from other sources to deal with your
            enquiries, consider your application, including carrying out credit searches, for
            marketing and to ensure that the services and advertising we offer are tailored to your
            needs and interests. We, potential lenders, insurers, service providers and agents may
            contact you by mail, telephone, email, SMS or other electronic messaging service with
            offers of products or services that may be of interest to you. By providing us with your
            contact details, you agree to being contacted by the methods and for the purposes set
            out in, and in accordance with, our{' '}
            <Link href={PRIVACY_POLICY_URL} role="link">
              Privacy Policy
            </Link>
            . In assessing your car loan application, we will make enquiries about you including
            searching your record with the credit reference agencies. You consent to us disclosing
            details of your finance application and we may use a &lsquo;credit scoring&rsquo; or
            other automated process in deciding whether to accept your application. Your information
            may also be sent to a 3rd party lender for further loan options who may also search your
            record with the credit reference agencies. If false or inaccurate information is provided
            and fraud is identified, details will be passed to fraud prevention agencies to prevent
            fraud and money laundering. Further details explaining how the information held by fraud
            prevention agencies may be used can be obtained by contacting us on{' '}
            <Link href="tel:01625619944" role="link">
              01625 619 944
            </Link>
            .
          </>
        }
      />

      <HeadingWithList
        title="Disclaimer/Liability"
        align="left"
        styleOverrides={{ marginBottom: 8 }}
        sectionData={{ items: disclaimerListItems }}
      />

      <HeadingWithParagraphs
        title="Privacy Policy"
        styleOverrides={{ marginBottom: 8 }}
        paragraphs={
          <>
            If you provide information to Zuto on or through this Site, Zuto will use it in
            accordance with the terms of its Privacy Policy. That Privacy Policy forms part of these
            Terms and Conditions and by using this Site you acknowledge and agree that you accept the
            terms of that{' '}
            <Link href={PRIVACY_POLICY_URL} role="link">
              Privacy Policy
            </Link>
            .
          </>
        }
      />

      <HeadingWithParagraphs
        title="General"
        paragraphs={[
          'These Terms and Conditions (as amended from time to time) constitute the entire agreement between you and Zuto concerning your use of this Site and supersede any previous arrangement, agreement, undertaking or proposal, written or oral between you and Zuto in relation to such matters. Zuto reserves the right to update these Terms and Conditions from time to time. If it does so, the updated version will be effective as soon as it is uploaded on to this Site and your continued use of the Site will constitute your acceptance of such updated Terms and Conditions. No other variation to these Terms and Conditions shall be effective unless in writing and signed by an authorised representative on behalf of Zuto. You should check the Terms and Conditions periodically to ensure that you are aware of and complying with the current version.',
          "These Terms and Conditions shall be governed by and construed in accordance with English law and you agree to submit to the exclusive jurisdiction of the English Courts. If any provision(s) of these Terms and Conditions is held by a court of competent jurisdiction to be invalid or unenforceable, then such provision(s) shall be construed, as nearly as possible, to reflect the intentions of the parties and all other provisions shall remain in full force and effect. Zuto's failure to exercise or enforce any right or provision of these Terms and Conditions shall not constitute a waiver of such right or provision unless acknowledged and agreed to by Zuto in writing. Unless otherwise expressly stated, nothing in the Terms and Conditions shall create any rights or any other benefits whether pursuant to the Contracts (Rights of Third Parties) Act 1999 or otherwise in favour of any person other than you, Zuto and its group of companies."
        ]}
      />

      <CarBackdrop />
    </>
  )
}

export default PageContent
